// Wave 20.17 — Liability release accept gate.
//
// Wrap a Start-like button flow. On first paint the hook fetches
// GET /legal/current?kind=liability and stores acceptance status. The
// consumer calls guard(onProceed) instead of onProceed; if the user
// has already accepted the CURRENT version, guard fires onProceed
// synchronously. Otherwise it opens a modal blocking the flow — the
// user reads the doc and clicks Accept, which POSTs /legal/accept
// and then fires the deferred onProceed.
//
// Also renders a small warning line the consumer can drop above the
// Start button when acceptance is pending, so the user knows why
// clicking Start opened a modal.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { colors } from '../theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

interface LegalDoc {
  kind: 'liability' | 'privacy';
  version: string;
  effectiveAt: string;
  body: string;
  mine: { accepted: boolean; lastAcceptedVersion: string | null };
}

async function fetchLegal(kind: 'liability' | 'privacy'): Promise<LegalDoc | null> {
  const jwt = localStorage.getItem(TOKEN_KEY);
  if (!jwt) return null;
  try {
    const res = await fetch(`${API}/legal/current?kind=${kind}`, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

async function acceptLegal(kind: 'liability' | 'privacy', version: string) {
  const jwt = localStorage.getItem(TOKEN_KEY);
  if (!jwt) throw new Error('not signed in');
  const res = await fetch(`${API}/legal/accept`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify({ kind, version }),
  });
  if (!res.ok) throw new Error(`${res.status}`);
}

/** State + trigger for a liability-gated Start button. */
export function useLiabilityGate() {
  const [doc, setDoc] = useState<LegalDoc | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [pendingProceed, setPendingProceed] = useState<(() => void) | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Wave 20.44 — track fetch failure so the modal can render a
  // helpful error state (with retry) instead of an empty waiver +
  // dead Accept button when the initial fetch silently returned
  // null.
  const [fetchFailed, setFetchFailed] = useState(false);

  const loadDoc = useCallback(async (): Promise<LegalDoc | null> => {
    const d = await fetchLegal('liability');
    if (d) {
      setDoc(d);
      setFetchFailed(false);
    } else {
      setFetchFailed(true);
    }
    setLoaded(true);
    return d;
  }, []);

  useEffect(() => {
    void loadDoc();
  }, [loadDoc]);

  const guard = useCallback(
    async (proceed: () => void) => {
      // Fetch still in flight — block Start. Rare (the hook fetches
      // on mount) but possible if a rider double-taps on cold load.
      if (!loaded) return;
      // If the initial fetch silently failed (network blip, JWT
      // expiry, etc.), the modal used to open empty with a dead
      // Accept. Retry inline first; only surface the error state if
      // the retry also fails.
      let current = doc;
      if (!current) {
        current = await loadDoc();
      }
      if (!current) {
        // Persist a helpful error into the modal instead of a
        // silent blank body.
        setError('Unable to load the current waiver. Check your connection and try again.');
        setPendingProceed(() => proceed);
        setOpen(true);
        return;
      }
      if (current.mine.accepted) {
        proceed();
        return;
      }
      setPendingProceed(() => proceed);
      setError(null);
      setOpen(true);
    },
    [doc, loaded, loadDoc],
  );

  const cancel = useCallback(() => {
    setOpen(false);
    setPendingProceed(null);
  }, []);

  const retry = useCallback(async () => {
    setError(null);
    const d = await loadDoc();
    if (!d) {
      setError('Still unable to load the waiver. Check your connection.');
    }
  }, [loadDoc]);

  const accept = useCallback(async () => {
    if (!doc) {
      // Don't silently no-op — nudge the rider toward retry.
      setError('The waiver failed to load. Tap Retry, or reload the page.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await acceptLegal('liability', doc.version);
      // Optimistically flip acceptance so a second Start click passes
      // through without re-fetching.
      setDoc({ ...doc, mine: { accepted: true, lastAcceptedVersion: doc.version } });
      setOpen(false);
      const proceed = pendingProceed;
      setPendingProceed(null);
      proceed?.();
    } catch (e: any) {
      setError(e?.message || 'Accept failed');
    } finally {
      setSaving(false);
    }
  }, [doc, pendingProceed]);

  const needsAccept = loaded && !!doc && !doc.mine.accepted;
  void fetchFailed; // reserved for future Preflight banner

  const Modal = (
    <Dialog
      open={open}
      onClose={saving ? undefined : cancel}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            backgroundColor: colors.surfaceElevated,
            border: `1px solid ${colors.border}`,
          },
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>Liability Release &amp; Waiver</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', mb: 2 }}>
          You must accept this release before starting a session.
          {doc?.mine.lastAcceptedVersion && doc.mine.lastAcceptedVersion !== doc.version && (
            <> This release has been updated since you last accepted; please review and re-accept.</>
          )}
        </Typography>
        {doc?.body ? (
          <Box
            sx={{
              maxHeight: 360,
              overflowY: 'auto',
              p: 2,
              borderRadius: 2,
              backgroundColor: colors.bg,
              border: `1px solid ${colors.border}`,
              whiteSpace: 'pre-wrap',
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.82rem',
              lineHeight: 1.6,
              color: colors.text,
            }}
          >
            {doc.body}
          </Box>
        ) : (
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: colors.bg,
              border: `1px solid ${colors.danger}66`,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ color: colors.danger, fontWeight: 700, fontSize: '0.9rem', mb: 1 }}>
              Couldn't load the current waiver.
            </Typography>
            <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem', mb: 2 }}>
              Check your connection and tap Retry, or reload the page.
            </Typography>
            <Button
              variant="outlined"
              onClick={retry}
              disabled={saving}
              sx={{ borderColor: colors.brand, color: colors.brand, fontWeight: 700 }}
            >
              Retry
            </Button>
          </Box>
        )}
        {error && (
          <Typography sx={{ color: colors.danger, mt: 2, fontSize: '0.85rem' }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={cancel} disabled={saving} sx={{ color: colors.textMuted }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={accept}
          disabled={saving || !doc?.body}
          sx={{
            backgroundColor: colors.brand,
            color: colors.textInverse,
            '&:hover': { backgroundColor: colors.brandBright },
            '&.Mui-disabled': { backgroundColor: colors.surfaceElevated, color: colors.textFaint },
          }}
        >
          {saving ? <CircularProgress size={18} /> : 'I agree'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { needsAccept, loaded, guard, Modal };
}
